/*
 * jj-line-item-properties.js
 * --------------------------------------------------------------------------
 * Acumatica ERP line-item integration for the Hyper (James 3.0) theme.
 *
 * Two responsibilities:
 *
 *  1. <jj-line-item-properties> custom element
 *     Keeps the _options line-item property in sync with the selected variant.
 *     (Hyper does not re-render the product form on variant change, so the
 *     Liquid-rendered _options inputs would otherwise go stale.)
 *
 *  2. Nested-object cart submission interceptor  <-- the Acumatica fix
 *     The legacy Evergreen (Vue) theme posted line-item properties to
 *     /cart/add.js as a JSON body with _options as a NESTED OBJECT:
 *         { items: [{ id, quantity, properties: { _options: {..}, _legacy_id }}] }
 *     Acumatica's relay (a Ruby lambda using oj/shopify_api) reads _options
 *     back, parses it, and matches each Acumatica configuration Feature/Option
 *     against the _options key/values by name. See the Carpenter James docs:
 *     ConfigurationEntry errors ShopifyPropertyNotFound / ShopifyOptionNotFound
 *     fire when _options can't be parsed into a proper object.
 *
 *     Hyper submits the product form via `new FormData(this)` with
 *     bracket-notation fields (properties[_options][Finish Color]=...). Although
 *     that displays identically in the Shopify admin, it is stored/serialized
 *     differently in the orders/create webhook than the legacy nested-object
 *     submission, and the relay's parser can't read it -> configuration fails ->
 *     Acumatica rejects the order.
 *
 *     This interceptor reproduces the legacy submission EXACTLY: it builds the
 *     same JSON body (with _options as a nested object) and posts it to
 *     /cart/add.js, then hands off to Hyper's own ProductForm instance methods
 *     to refresh the cart drawer — so nothing else in the cart flow changes.
 *
 *     It ONLY activates for product forms that carry _options (configured
 *     products). Everything else stays on Hyper's native FormData path.
 *
 *  3. Product add-on line items  (snippets/product-addons.liquid)
 *     The same interceptor also appends any add-ons the customer selected in a
 *     sibling <product-addons> block as additional /cart/add.js line items, so
 *     the parent product and its add-ons are added in a single request. Add-on
 *     lines carry no line-item properties (no _options) — they sync to
 *     Acumatica as ordinary product lines. The interceptor now activates when
 *     _options is present OR when at least one add-on is selected; with neither,
 *     the native FormData path is used exactly as before.
 *
 *     Cart-type modes: in drawer mode the cart drawer is refreshed/opened as
 *     usual; in page mode (settings > cart type = "page") we reproduce Hyper's
 *     native end state by AJAX-adding everything and then navigating to /cart,
 *     so add-ons are never dropped on the way to the full cart page.
 * --------------------------------------------------------------------------
 */
(function () {
  /* ---------- 1. Variant -> _options sync element ---------- */
  if (!(window.customElements && customElements.get('jj-line-item-properties'))) {
    class JJLineItemProperties extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId || null;
        if (
          window.FoxTheme &&
          FoxTheme.pubsub &&
          FoxTheme.pubsub.PUB_SUB_EVENTS &&
          typeof FoxTheme.pubsub.subscribe === 'function'
        ) {
          this.unsubscribe = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this)
          );
        }
      }
      disconnectedCallback() {
        if (typeof this.unsubscribe === 'function') this.unsubscribe();
      }
      onVariantChange(event) {
        const data = event && event.data;
        if (!data || !data.variant) return;
        if (this.sectionId && data.sectionId && String(data.sectionId) !== String(this.sectionId)) return;
        const optionValues = data.variant.options || [];
        this.querySelectorAll('input[data-jj-option-index]').forEach((input) => {
          const index = parseInt(input.dataset.jjOptionIndex, 10);
          if (Number.isNaN(index)) return;
          const value = optionValues[index];
          if (value !== undefined && value !== null) input.value = value;
        });
      }
    }
    if (window.customElements) customElements.define('jj-line-item-properties', JJLineItemProperties);
  }

  /* ---------- 2. Nested-object cart submission interceptor ---------- */
  if (window.__jjCartInterceptorInstalled) return;
  window.__jjCartInterceptorInstalled = true;

  // Build a /cart/add.js JSON body from the product form's FormData, with
  // _options rebuilt as a NESTED OBJECT. Mirrors the legacy { ..._options,
  // _legacy_id } property order.
  function jjBuildCartAddBody(formData, sections, sectionsUrl, extraItems) {
    const options = {};
    const otherProps = {};
    let id = null;
    let quantity = 1;
    let sellingPlan = null;

    for (const [key, value] of formData.entries()) {
      if (key === 'id') {
        id = value;
      } else if (key === 'quantity') {
        const q = parseInt(value, 10);
        if (!Number.isNaN(q)) quantity = q;
      } else if (key === 'selling_plan') {
        if (value) sellingPlan = value;
      } else {
        const optMatch = key.match(/^properties\[_options\]\[(.+)\]$/);
        if (optMatch) {
          options[optMatch[1]] = value;
          continue;
        }
        const propMatch = key.match(/^properties\[(.+)\]$/);
        if (propMatch) otherProps[propMatch[1]] = value;
      }
    }

    if (!id) return null; // no variant resolved -> let Hyper handle it

    // Property ORDER matches the order that PASSED Acumatica (#39750), which
    // lists _options FIRST, then _legacy_id. (Order is not what Acumatica keys
    // on — a paid order passes and a $0 order fails regardless of order — but
    // mirroring the known-good order avoids it being a variable at all.)
    const properties = {};
    if (Object.keys(options).length) properties._options = options;
    Object.assign(properties, otherProps); // _legacy_id, gift-card props, etc.

    const item = { id: id, quantity: quantity, properties: properties };
    if (sellingPlan) item.selling_plan = sellingPlan;

    // Parent line first, then any selected add-on lines. Add-on items carry no
    // line-item properties — they sync to Acumatica as ordinary product lines.
    const items = [item];
    if (extraItems && extraItems.length) {
      for (let i = 0; i < extraItems.length; i++) items.push(extraItems[i]);
    }

    const body = { items: items };
    if (sections && sections.length) body.sections = sections;
    if (sectionsUrl) body.sections_url = sectionsUrl;
    return body;
  }

  // Collect selected add-on products for this product form. Add-on cards live
  // in a sibling <product-addons> block (rendered by snippets/product-addons.liquid),
  // so they are NOT in the form's FormData and must be read from the DOM,
  // scoped to the same product section as the form.
  function jjGetSectionRoot(form) {
    return (
      form.closest('.product__info-container') ||
      form.closest('[data-section]') ||
      form.closest('.shopify-section') ||
      document
    );
  }

  function jjCollectAddonItems(form) {
    const items = [];
    const root = jjGetSectionRoot(form);
    const checks = root.querySelectorAll('product-addons input[data-jj-addon]:checked');
    checks.forEach((cb) => {
      if (cb.disabled) return;
      const id = cb.getAttribute('data-addon-id');
      if (!id) return;
      let qty = parseInt(cb.getAttribute('data-addon-qty'), 10);
      if (Number.isNaN(qty) || qty < 1) qty = 1;
      items.push({ id: id, quantity: qty });
    });
    return items;
  }

  function jjHasSelectedAddons(form) {
    const root = jjGetSectionRoot(form);
    return !!root.querySelector('product-addons input[data-jj-addon]:checked:not([disabled])');
  }

  function jjSubmit(form, event) {
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

    // In page-cart mode there is no drawer to open — Hyper's native flow does a
    // full-page form POST to /cart/add and lands on /cart. We reproduce that end
    // state: AJAX-add parent + add-ons, then navigate to the cart page.
    const isPageCart = !!(window.FoxTheme && FoxTheme.settings && FoxTheme.settings.cartType === 'page');

    form.lastSubmittedElement = event.submitter || form;
    if (typeof form.displayFormErrors === 'function') form.displayFormErrors();

    // Gather cart sections to bundle, exactly like Hyper does.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const formData = new FormData(form);
    form.formData = formData; // handleCartError reads this.formData.get('id')

    const addonItems = jjCollectAddonItems(form);
    const body = jjBuildCartAddBody(formData, sectionsToBundle, window.location.pathname, addonItems);
    if (!body) {
      // Could not build a valid body — fall back to a native submit.
      form.submit();
      return;
    }

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    };

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('btn--loading');
    }

    fetch(`${FoxTheme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          if (typeof form.handleCartError === 'function') form.handleCartError(parsedState);
          if (isPageCart) return; // error shown inline; stay on the product page
          const cartJsonSub = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
          ).json();
          if (typeof form.updateCartState === 'function') form.updateCartState(cartJsonSub);
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
          return;
        }

        // Page-cart mode: go to the full cart page (no drawer to refresh).
        if (isPageCart) {
          window.location.href = FoxTheme.routes.cart_url;
          return;
        }

        const cartJson = await (
          await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
        ).json();
        cartJson['sections'] = parsedState['sections'];

        if (typeof form.updateCartState === 'function') form.updateCartState(cartJson);
        if (typeof form.dispatchProductAddedEvent === 'function') form.dispatchProductAddedEvent(parsedState);
        if (typeof form.showCartDrawer === 'function') form.showCartDrawer();
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        if (submitButton) {
          submitButton.classList.remove('btn--loading');
          submitButton.removeAttribute('aria-disabled');
        }
      });
  }

  // Capture-phase listener fires before Hyper's own (target-phase) submit
  // handler, so we can take over the submission for configured products and/or
  // products with selected add-ons, in both drawer and page cart modes.
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('is') !== 'product-form') return;

      // Required globals not ready -> let Hyper handle it natively.
      if (!window.FoxTheme || !FoxTheme.routes || !FoxTheme.routes.cart_add_url || !FoxTheme.utils) return;
      // NOTE: page-cart mode is intentionally NOT skipped here. When add-ons are
      // selected (or _options is present), we take over in both drawer and page
      // mode; jjSubmit() redirects to /cart afterwards in page mode. Only plain
      // products with neither fall through to Hyper's native path (below).

      // Intercept when either:
      //   (a) nested _options is present (configured products — the Acumatica
      //       path that must use the legacy nested-object submission), or
      //   (b) the customer has selected one or more add-ons (so they can be
      //       appended as extra /cart/add.js line items).
      // When neither applies, behaviour is byte-identical to before this change:
      // Hyper's native FormData path handles the add untouched.
      var hasOptions = !!form.querySelector('input[name^="properties[_options]"]');
      var hasAddons = jjHasSelectedAddons(form);
      if (!hasOptions && !hasAddons) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      try {
        jjSubmit(form, event);
      } catch (e) {
        console.log('[jj] nested-options submit failed, falling back', e);
        try {
          form.submit();
        } catch (_) {}
      }
    },
    true // capture
  );
})();